import axios, { AxiosInstance } from 'axios';
import config from '../config';
import { ApiErrorCode, GMAIL_AUTH_EXPIRED_EVENT, getApiErrorCode, isNotFoundError } from '../utils/errorUtils';
import { captureApiError, setMonitoringUser } from './monitoring';
import { captureEvent, resetAnalytics } from './analytics';
import { AUTO_LANGUAGE } from '../utils/languages';
// The two auth endpoints spell the Turnstile field differently and the login
// side fails SILENTLY when it is wrong, so the names live in exactly one place
// (beside the widget that produces the token) and are never inlined here.
import {
  TURNSTILE_TOKEN_FIELD_LOGIN,
  TURNSTILE_TOKEN_FIELD_REGISTRATION,
} from '../components/auth/TurnstileWidget';
import {
  Search,
  SearchListResponse,
  ResumeUploadResponse,
  CreateSearchRequest,
  CreateSearchResponse,
  CreateSearchPlanRequest,
  SearchPlanResponse,
  CreateSearchBatchRequest,
  CreateSearchBatchResponse,
  SearchQuota,
  EmailTemplate,
  ListParams,
  Opportunity,
  OpportunityListResponse,
  OpportunityListParams,
  RejectOpportunityRequest,
  RejectOpportunityResponse,
  RejectionListResponse,
  RejectionListParams,
  FilteredOpportunityListResponse,
  FilteredOpportunityListParams,
  ApproveOpportunityRequest,
  ApproveOpportunityResponse,
  ApplicationListParams,
  ApplicationListResponse,
  UpdateApplicationStatusRequest,
  UpdateApplicationStatusResponse,
  FollowUpDraftResponse,
  FollowUpRequest,
  FollowUpResponse,
  ReplyDraftResponse,
  ReplyRequest,
  ReplyResponse,
  RetryContactResponse,
  ArchiveApplicationRequest,
  ArchiveApplicationResponse,
  ArchiveListParams,
  ArchiveListResponse,
  DashboardStats,
  UserSettings,
  UserSettingsPatch,
  PendingEmailImport,
  AttachPendingEmailResponse,
  ResumeReview,
  ResumeReviewRequest,
  JobSearchParams,
  JobSearchResponse,
  DeclareApplicationRequest,
  DeclareApplicationResponse,
  IntroRequestDraftRequest,
  IntroRequestDraftResponse,
  ResumeVersion,
  ResumeContent,
  ResumeTheme,
  ResumeTemplateId,
  FixDecision,
  ResumeFixCard,
} from '../types';
import { SSETokenResponse } from '../types/notifications';
import {
  normalizeBlobDownloadError,
  parseContentDispositionFilename,
} from '../utils/download';

const BASE_URL = config.baseURL;

const api: AxiosInstance = axios.create({
    baseURL: BASE_URL,
  });

// Bare axios instance for endpoints that authenticate via path/query token
// (no Authorization header injection, no 401 redirect). Used by the
// manual-forward attach flow.
const publicApi: AxiosInstance = axios.create({
  baseURL: BASE_URL,
});

// Add a request interceptor to include the access token in the headers
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
            if(!config.headers.has('Authorization'))
              config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Re-entry guard so a failing DELETE doesn't loop the handler.
let gmailAuthExpiredHandled = false;

// Add a response interceptor to handle 401 errors (session expired)
// and to react to backend error_code === 'gmail_auth_expired'.
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401 && !error.config?.skipAuthRedirect) {
            const hadSession = !!localStorage.getItem('userId');

            // Clear auth data from localStorage
            localStorage.removeItem('userFirstName');
            localStorage.removeItem('accessToken');
            localStorage.removeItem('accessTokenExpiration');
            localStorage.removeItem('userId');
            localStorage.removeItem('userRole');
            localStorage.removeItem('adminAccessToken');
            localStorage.removeItem('adminAccessTokenExpiration');
            localStorage.removeItem('adminUserId');
            localStorage.removeItem('adminUserFirstName');
            localStorage.removeItem('impersonatedUsername');

            // Only an expiry of a real session is a "session_expired"; a 401 on the
            // login form is just bad credentials, not a dropped session.
            if (hadSession) {
                captureEvent('session_expired');
                resetAnalytics();
                setMonitoringUser(null);
            }

            // Redirect to signin page (only if not already on an auth/credential
            // page that handles its own 401s), preserving the current URL so the
            // user lands back here after re-auth. /reset-password and /forgot-password
            // are public auth pages: a 401 there is page-local, not a dropped session,
            // and the reset page already scrubs its token from the URL — redirecting
            // would strand the user on a tokenless dead-end.
            const currentPath = window.location.pathname;
            if (
                !currentPath.includes('/signin') &&
                !currentPath.includes('/register') &&
                !currentPath.includes('/verify') &&
                !currentPath.includes('/reset-password') &&
                !currentPath.includes('/forgot-password')
            ) {
                const target = window.location.pathname + window.location.search;
                window.location.href = `/signin?redirect=${encodeURIComponent(target)}`;
            }
        }

        if (getApiErrorCode(error) === ApiErrorCode.GmailAuthExpired && !gmailAuthExpiredHandled) {
            gmailAuthExpiredHandled = true;
            // Clean up the dead token row server-side. Fire-and-forget;
            // reset the guard once it settles so a future expiry can fire again.
            api.delete('/api/v1/oauth/gmail').catch(() => {}).finally(() => {
                gmailAuthExpiredHandled = false;
            });
            window.dispatchEvent(new CustomEvent(GMAIL_AUTH_EXPIRED_EVENT));
            captureEvent('gmail_auth_expired', { error_code: ApiErrorCode.GmailAuthExpired });
        }

        // Report to Sentry with structured tags (skips 401s and cancellations internally).
        captureApiError(error);

        // Mirror product-impacting backend errors to PostHog for funnel / UX analysis.
        const mirrorCode = getApiErrorCode(error);
        const mirrorEndpoint = error.config?.url ?? 'unknown';
        if (mirrorCode === ApiErrorCode.LlmFailed || mirrorCode === ApiErrorCode.LlmParsingFailed) {
            captureEvent('llm_generation_failed', { error_code: mirrorCode, endpoint: mirrorEndpoint });
        } else if (mirrorCode === ApiErrorCode.GmailSendError) {
            captureEvent('gmail_send_failed', { error_code: mirrorCode, endpoint: mirrorEndpoint });
        } else if (mirrorCode === ApiErrorCode.TurnstileVerificationFailed) {
            // The only quantitative signal for the enforcement rollout. Sentry
            // cannot supply it: the backend suppresses this error and so do the
            // token-carrying calls above, and a rate is what the switch-on needs
            // anyway — "5% of signups" is a working control, "100%" is a broken
            // sitekey, and a raw event count cannot tell those apart. `endpoint`
            // separates the two flows, and singles out the app-credential login
            // (the allowlist fault) from real users.
            captureEvent('turnstile_rejected', { error_code: mirrorCode, endpoint: mirrorEndpoint });
        }

        return Promise.reject(error);
    }
);

// Interfaces for request and response data types
export interface LoginRequest {
  username: string;
  password: string;
}

interface LoginResponse {
  accessToken: string;
  tokenType: string;
  expiration: string;
}

export interface UserInDTO {
  username: string;
  firstName: string;
  lastName: string;
  password: string;
  phone?: string;
  linkedinUrl?: string;
}

interface UserOutDTO {
  id: string;
  createdAt: string;
  createdAtTimestamp: number;
}

interface UserRegistrationOutDTO {
  id: string;
  verificationURL: string;
  createdAt: string;
  createdAtTimestamp: number;
}

// PUT /v1/users/{id} is a FULL REPLACE, not a patch: the server's
// UserUpdateInDTO inherits linkedinUrl and phone from StandardUserUpdateInDTO,
// both defaulting to None, and BaseDBModel.assign iterates model_dump() with no
// exclude_unset -- so any field omitted here is written as NULL over whatever
// the column held. Both are declared nullable server-side; typing them as
// optional-and-nullable here is what makes an omission visible at the call site
// rather than silently destructive.
export interface UserUpdateInDTO {
  username: string;
  role: string;
  firstName: string;
  lastName: string;
  disabled: boolean;
  linkedinUrl?: string | null;
  phone?: string | null;
}

export interface StandardUserUpdateInDTO {
  firstName: string;
  lastName: string;
  linkedinUrl?: string;
  phone?: string;
}

export interface StandardUserOutDTO {
  id: string;
  username: string;
  role: string;
  firstName: string;
  lastName: string;
  linkedinUrl: string;
  phone: string;
  createdAt: string;
  createdAtTimestamp: number;
}

export interface FullUserOutDTO {
  id: string;
  username: string;
  role: string;
  firstName: string;
  lastName: string;
  linkedinUrl: string;
  phone: string;
  disabled: boolean;
  verified: boolean;
  createdAt: string;
  createdAtTimestamp: number;
}

interface UserListOutDTO {
  count: number;
  users: FullUserOutDTO[];
  createdAt: string;
  createdAtTimestamp: number;
}

interface UserDeletionOutDTO {
  id: string;
  deletedAt: string;
  createdAt: string;
  createdAtTimestamp: number;
}

interface EmailVerificationOutDTO {
  verificationID: string;
  userID: string;
  accessToken: string;
  accessTokenExpiration: string;
  createdAt: string;
  createdAtTimestamp: number;
}

/**
 * Turnstile outcomes that are NOT worth a Sentry event on a request that
 * carried a user-solved token.
 *
 *   turnstile_verification_failed — a blocked bot is the control working, at bot
 *     volume it would bury real errors, and the backend already opts out
 *     (REPORT_TO_SENTRY = False on TurnstileVerificationFailedError).
 *
 *   turnstile_unreachable — a DELIBERATE DIVERGENCE from the backend, which does
 *     report this one (TurnstileUnreachableError inherits REPORT_TO_SENTRY =
 *     True from ServiceUnavailableError). A siteverify outage is already paged
 *     from the API side; mirroring it from every browser adds a flood of
 *     duplicates of an incident that is by then well known, and the plan flags
 *     Sentry volume during exactly that outage as a risk.
 *
 * turnstile_misconfigured is deliberately absent: an unset TURNSTILE_SECRET_KEY
 * is our deploy error, nobody else is watching for it, and it is silent from the
 * user's side.
 *
 * None of this suppresses the PostHog mirror below — during the enforcement
 * rollout what matters is the RATE, which Sentry events could never give.
 */
const TURNSTILE_EXPECTED_ERROR_CODES = [
  ApiErrorCode.TurnstileVerificationFailed,
  ApiErrorCode.TurnstileUnreachable,
];

// API functions for each endpoint
/**
 * @param turnstileToken Cloudflare Turnstile token for a human sign-in. Optional
 *   ONLY because the app-credential login (utils/appToken) has no widget to get
 *   one from and is exempted server-side by an allowlist on its username; every
 *   user-facing caller must pass one. `OAuth2PasswordRequestForm` drops unknown
 *   form fields, so a missing or misnamed token is not a validation error — it
 *   is a 403 `turnstile_verification_failed` that looks exactly like a blocked
 *   bot, with nothing in Sentry.
 */
export const login = async (data: LoginRequest, turnstileToken?: string): Promise<LoginResponse> => {
  const formData = new URLSearchParams();
  formData.append('username', data.username);
  formData.append('password', data.password);
  formData.append('grant_type', 'password');
  if (turnstileToken) {
    formData.append(TURNSTILE_TOKEN_FIELD_LOGIN, turnstileToken);
  }

  const response = await api.post('/api/v1/oauth/token', formData, {
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded',
    },
    // Only when this call carried a user-solved token. That is the whole
    // discriminator: without it, the app-credential login below reports its 403
    // to Sentry by construction, which is the one Turnstile 403 nobody else
    // reports — a broken TURNSTILE_EXEMPT_USERNAMES takes down signup, password
    // reset and the website contact form at once, and the backend keeps quiet
    // about it (REPORT_TO_SENTRY = False).
    ...(turnstileToken ? { expectedErrorCodes: TURNSTILE_EXPECTED_ERROR_CODES } : {}),
  });
  return response.data;
};

export const loginTargetUser = async (targetUserId: string, token: string | undefined): Promise<LoginResponse> => {
  const response = await api.post(`/api/v1/oauth/token/users/${targetUserId}`, null, {
    params: {
      grant_type: 'impersonate',
    },
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  });
  return response.data;
};

/**
 * The self-service signup endpoint. Exported because CreateAccount has to tell a
 * 403 raised by THIS request from one raised by the app-credential login it also
 * makes, which is a different problem with the same error_code.
 */
export const REGISTRATIONS_PATH = '/api/v1/users/registrations';

/**
 * @param token App token (the registration endpoint's bearer credential).
 * @param turnstileToken Cloudflare Turnstile token. Required rather than
 *   optional so forgetting it is a compile error: the registration DTO is
 *   `extra = "forbid"`, so the field name has to be exact, and an absent token
 *   is a 403.
 */
export const createUser = async (
    data: UserInDTO,
    token: string,
    turnstileToken: string,
): Promise<UserRegistrationOutDTO> => {
    const response = await api.post(
        REGISTRATIONS_PATH,
        { ...data, [TURNSTILE_TOKEN_FIELD_REGISTRATION]: turnstileToken },
        {
            headers: {
                Authorization: `Bearer ${token}`,
            },
            // Scoped to the two codes, not to the whole 403: this endpoint is
            // role-gated, so an access_denied 403 here means the app account
            // lost its APP role — a total signup outage that must still page.
            expectedErrorCodes: TURNSTILE_EXPECTED_ERROR_CODES,
        },
    );
    return response.data;
};  

export const findUsers = async (
  params: {
    skip?: number;
    limit?: number;
    sort_field?: string;
    sort_method?: string;
    with_deleted?: boolean;
    role?: string;
  } = {},
  token?: string | undefined
): Promise<UserListOutDTO> => {
  const response = await api.get('/api/v1/users/', {
    params,
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  });
  return response.data;
};

export const getMe = async (): Promise<StandardUserOutDTO> => {
  const response = await api.get('/api/v1/users/me');
  return response.data;
};

export const deleteMe = async (): Promise<UserDeletionOutDTO> => {
  const response = await api.delete('/api/v1/users/me');
  return response.data;
};

export const getUser = async (userId: string): Promise<FullUserOutDTO> => {
  const response = await api.get(`/api/v1/users/${userId}`);
  return response.data;
};

export const updateUser = async (
  userId: string,
  data: UserUpdateInDTO,
  token?: string
): Promise<UserOutDTO> => {
  const response = await api.put(`/api/v1/users/${userId}`, data, {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  });
  return response.data;
};

export const updateMe = async (data: StandardUserUpdateInDTO): Promise<StandardUserOutDTO> => {
  const response = await api.put(`/api/v1/users/me`, data)
  return response.data;
};


export const deleteUser = async (userId: string, token?: string): Promise<UserDeletionOutDTO> => {
  const response = await api.delete(`/api/v1/users/${userId}`, {
    headers: {
      Authorization: token ? `Bearer ${token}` : undefined,
    },
  });
  return response.data;
};

export const registerUser = async (data: UserInDTO): Promise<UserRegistrationOutDTO> => {
  const response = await api.post('/api/v1/users/registrations', data);
  return response.data;
};

export const resendVerificationEmail = async (userId: string, accessToken: string): Promise<UserRegistrationOutDTO> => {
  const response = await api.post(
    `/api/v1/users/registrations/verifications/email/${userId}`,
    null,
    {
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    }
  );
  return response.data;
};

export const verifyEmail = async (token: string): Promise<EmailVerificationOutDTO> => {
  const response = await api.post('/api/v1/users/registrations/verifications', null, {
    params: { token },
  });
  return response.data;
};

// ============ PASSWORD ============

/**
 * Change the logged-in user's password. 204 on success (no body).
 * `skipAuthRedirect` so a wrong-current-password 401 surfaces to the caller
 * instead of tearing down the (still-valid) session.
 */
export const changeMyPassword = async (currentPassword: string, newPassword: string): Promise<void> => {
  await api.post('/api/v1/users/me/password', { currentPassword, newPassword }, { skipAuthRedirect: true });
};

/**
 * Request a password-reset email. Always 200 (anti-enumeration). Authenticated by
 * the APP token (the user is logged out); `skipAuthRedirect` because a 401 here is a
 * stale app token, not a user session expiry — the caller retries with a fresh one.
 */
export const requestPasswordReset = async (email: string, appToken: string): Promise<void> => {
  await api.post('/api/v1/users/passwords/reset-requests', { email }, {
    headers: { Authorization: `Bearer ${appToken}` },
    skipAuthRedirect: true,
  });
};

/**
 * Complete a password reset with the emailed token. Public (no auth header) → use
 * `publicApi` so the URL token never reaches the interceptor/Sentry. Returns a fresh
 * session the caller persists to log the user straight in.
 */
export const resetPassword = async (
  token: string,
  newPassword: string,
): Promise<{ accessToken: string; accessTokenExpiration: string }> => {
  const response = await publicApi.post('/api/v1/users/passwords/resets', { token, newPassword });
  return response.data;
};

// ============ SEARCHES API ============

export const uploadResume = async (file: File): Promise<ResumeUploadResponse> => {
  const formData = new FormData();
  formData.append('resume_file', file);

  const response = await api.post('/api/v1/searches/resumes', formData);
  return response.data;
};

export const createSearch = async (
  resumeInternalId: string,
  data: CreateSearchRequest
): Promise<CreateSearchResponse> => {
  // Trailing slash is required by the backend route; `data` carries the optional
  // camelCase `draftLanguage`.
  const response = await api.post('/api/v1/searches/', data, {
    params: { resume_internal_id: resumeInternalId },
  });
  return response.data;
};

/**
 * Multi-Agent Search — generate a coverage plan (3–5 proposed parallel searches)
 * from the user's seed role+location. Stateless: creates nothing and does NOT
 * consume the resume; the user reviews/edits the items client-side and posts the
 * kept ones to `createSearchBatch`. The response embeds the current `quota`
 * (caps are dynamic — read them, never hardcode).
 */
export const createSearchPlan = async (
  data: CreateSearchPlanRequest
): Promise<SearchPlanResponse> => {
  const response = await api.post('/api/v1/searches/plan', data);
  return response.data;
};

/**
 * Multi-Agent Search — create a pack of searches in one call. Partial success:
 * every item reports its own `status` (created | skipped_duplicate | skipped_limit),
 * in request order. Consumes the resume once ≥1 search is created (404 thereafter);
 * if nothing is created the resume is kept so the user can retry. Returns the shared
 * `groupId` and the post-creation `quota`.
 */
export const createSearchBatch = async (
  data: CreateSearchBatchRequest
): Promise<CreateSearchBatchResponse> => {
  const response = await api.post('/api/v1/searches/batch', data);
  return response.data;
};

/**
 * Current search-slot quota. Use to gate "New search" / "add another" and to show
 * slot counts. Caps are dynamic — always read them here; never hardcode 5/10.
 */
export const getSearchQuota = async (): Promise<SearchQuota> => {
  const response = await api.get('/api/v1/searches/quota');
  return response.data;
};

export const reviewResume = async (
  resumeInternalId: string,
  data: ResumeReviewRequest
): Promise<ResumeReview> => {
  // Advisory resume review against the user-confirmed role/location. Backend
  // memoizes (resumeInternalId + role + location), so repeat calls are free.
  const response = await api.post(
    `/api/v1/searches/resumes/${resumeInternalId}/review`,
    data
  );
  return response.data;
};

export const getSearches = async (params: ListParams = {}): Promise<SearchListResponse> => {
  const response = await api.get('/api/v1/searches', { params });
  return response.data;
};

export const getSearch = async (searchId: string): Promise<Search> => {
  const response = await api.get(`/api/v1/searches/${searchId}`);
  return response.data;
};

/**
 * Soft-delete a search the user owns. The backend immediately hides it and all its
 * children (opportunities/applications/rejections/archives) from every endpoint.
 * 204 (deleted) and 404 (missing / not-owned / already gone) are BOTH success from the
 * UI's view — both mean "it's no longer there." 401 is handled globally by the response
 * interceptor (clears auth → /signin). Idempotent + retry-safe by construction.
 */
export const deleteSearch = async (searchId: string): Promise<void> => {
  try {
    // 404 here is a benign, expected outcome (already gone / stale list / idempotent
    // re-delete from another tab), so keep it out of Sentry error reporting.
    await api.delete(`/api/v1/searches/${searchId}`, { expectedStatuses: [404] });
  } catch (error) {
    if (isNotFoundError(error)) return; // already gone = success
    throw error;
  }
};

// ============ LOCATION ENDPOINTS (deep-link page resolution) ============

export interface SearchLocationResponse {
  page: number;
}

export interface ApplicationLocationResponse {
  bucket: 'active' | 'archived';
  page: number;
}

// Regular and rejected share the opportunity_id space; filtered is a separate
// entity (filtered_opportunity_id) that can't promote to a regular opportunity,
// so it has its own endpoint.
export interface OpportunityLocationResponse {
  bucket: 'regular' | 'rejected';
  page: number;
}

export interface FilteredOpportunityLocationResponse {
  page: number;
}

export const getSearchLocation = async (
  searchId: string,
  pageSize: number
): Promise<SearchLocationResponse> => {
  const response = await api.get(`/api/v1/searches/${searchId}/location`, {
    params: { page_size: pageSize },
  });
  return response.data;
};

export const getApplicationLocation = async (
  applicationId: string,
  pageSize: number
): Promise<ApplicationLocationResponse> => {
  const response = await api.get(`/api/v1/jobs/applications/${applicationId}/location`, {
    params: { page_size: pageSize },
  });
  return response.data;
};

// The locator counts the rows ordered before `opportunityId` under the list's
// own ordering, so it MUST be given the same session anchor the list is paging
// with — otherwise it computes a page from a different unread/read grouping.
export const getOpportunityLocation = async (
  opportunityId: string,
  pageSize: number,
  unreadAnchor?: string
): Promise<OpportunityLocationResponse> => {
  const response = await api.get(`/api/v1/jobs/opportunities/${opportunityId}/location`, {
    params: { page_size: pageSize, unread_anchor: unreadAnchor },
  });
  return response.data;
};

export const getFilteredOpportunityLocation = async (
  filteredOpportunityId: string,
  pageSize: number
): Promise<FilteredOpportunityLocationResponse> => {
  const response = await api.get(
    `/api/v1/jobs/filtered-opportunities/${filteredOpportunityId}/location`,
    { params: { page_size: pageSize } }
  );
  return response.data;
};

export const updateSearchStatus = async (
  searchId: string,
  status: 'paused' | 'active'
): Promise<Search> => {
  const response = await api.patch(`/api/v1/searches/${searchId}/status`, { status });
  return response.data;
};

// ============ JOBS API ============

export const getOpportunities = async (
  params: OpportunityListParams = {}
): Promise<OpportunityListResponse> => {
  const response = await api.get('/api/v1/jobs/opportunities', { params });
  return response.data;
};

export const getOpportunity = async (opportunityId: string): Promise<Opportunity> => {
  const response = await api.get(`/api/v1/jobs/opportunities/${opportunityId}`);
  return response.data;
};

// Mark an opportunity as read (email-style inbox, design §14). Fired when the
// drawer opens for an unread row. Idempotent server-side (first call stamps
// viewedAt, repeats keep it) and 204 — fire-and-forget from the caller.
export const markOpportunityViewed = async (opportunityId: string): Promise<void> => {
  await api.post(`/api/v1/jobs/opportunities/${opportunityId}/view`);
};

// Save / unsave an opportunity — the "come back to this" marker. Idempotent
// server-side in both directions, 204 either way. Unlike markOpportunityViewed
// this is an explicit user gesture, so callers must NOT swallow the rejection:
// a failed save has to roll the optimistic fill back.
export const saveOpportunity = async (opportunityId: string): Promise<void> => {
  await api.post(`/api/v1/jobs/opportunities/${opportunityId}/save`);
};

export const unsaveOpportunity = async (opportunityId: string): Promise<void> => {
  await api.delete(`/api/v1/jobs/opportunities/${opportunityId}/save`);
};

// Preview of the tailored filename the user's resume will be attached as when the
// approval email is sent (e.g. "Joe_Doe_Senior_Product_Manager_Resume_2026.pdf").
// The value is produced by the same server code that names the real attachment, so
// it is byte-identical to what the recipient receives — never reconstruct it FE-side.
// `resumeAttachmentName` is null only when the search has no resume (edge case).
export interface ResumeAttachmentNameResponse {
  resumeAttachmentName: string | null;
}

export const getResumeAttachmentName = async (
  opportunityId: string
): Promise<ResumeAttachmentNameResponse> => {
  // A 404 here is benign and expected — the endpoint may not be deployed yet, or the
  // opportunity was deleted/archived in another tab. The drawer silently falls back to
  // the generic "resume.pdf" label, so keep these out of Sentry (real 500s / network
  // errors still report). expectedStatuses only gates reporting — the promise still
  // rejects, so the caller's .catch runs and the fallback holds.
  const response = await api.get(
    `/api/v1/jobs/opportunities/${opportunityId}/resume-attachment-name`,
    { expectedStatuses: [404] }
  );
  return response.data;
};

export const rejectOpportunity = async (
  opportunityId: string,
  data: RejectOpportunityRequest
): Promise<RejectOpportunityResponse> => {
  const response = await api.post(`/api/v1/jobs/opportunities/${opportunityId}/reject`, data);
  return response.data;
};

export const undoRejectOpportunity = async (
  opportunityId: string
): Promise<Opportunity> => {
  const response = await api.delete(`/api/v1/jobs/opportunities/${opportunityId}/reject`);
  return response.data;
};

export const getRejections = async (
  params: RejectionListParams = {}
): Promise<RejectionListResponse> => {
  const response = await api.get('/api/v1/jobs/rejections', { params });
  return response.data;
};

export const getFilteredOpportunities = async (
  params: FilteredOpportunityListParams = {}
): Promise<FilteredOpportunityListResponse> => {
  const response = await api.get('/api/v1/jobs/filtered-opportunities', { params });
  return response.data;
};

// ============ GMAIL OAUTH API ============

export interface GmailStatusResponse {
  connected: boolean;
  // False when the user connected but left Google's "Send email on your
  // behalf" checkbox unchecked — connected, yet we can't send applications.
  // Optional: older backend payloads omit it; consumers treat absent as fine.
  canSend?: boolean;
  gmailAddress: string | null;
}

export interface GmailAuthUrlResponse {
  authUrl: string;
}

export const getGmailStatus = async (): Promise<GmailStatusResponse> => {
  const response = await api.get('/api/v1/oauth/gmail/status');
  return response.data;
};

export const getGmailAuthUrl = async (): Promise<GmailAuthUrlResponse> => {
  const response = await api.get('/api/v1/oauth/gmail/authorize');
  return response.data;
};

export const disconnectGmail = async (): Promise<void> => {
  await api.delete('/api/v1/oauth/gmail');
};

// ============ SUBMISSIONS API ============

export const approveOpportunity = async (
  opportunityId: string,
  data: ApproveOpportunityRequest
): Promise<ApproveOpportunityResponse> => {
  const response = await api.post(`/api/v1/jobs/opportunities/${opportunityId}/approve`, data);
  return response.data;
};

// ============ WARM REFERRAL API ============

// Log an application the user already made OUTSIDE Beast (referral or direct).
// No email is sent; the opportunity flips to APPLIED. NOT idempotent — a second
// declare returns 409 ("already been submitted") — treat as already-applied.
export const declareApplication = async (
  opportunityId: string,
  data: DeclareApplicationRequest
): Promise<DeclareApplicationResponse> => {
  const response = await api.post(
    `/api/v1/jobs/opportunities/${opportunityId}/declare-application`,
    data
  );
  return response.data;
};

// Generate an "ask for a vouch" intro request: a note to the user's connection
// + a forwardable factual blurb. Live LLM call (seconds) — stateless, stores
// nothing; regenerate and retry-after-500 are both just another call.
export const getIntroRequestDraft = async (
  opportunityId: string,
  data: IntroRequestDraftRequest = {}
): Promise<IntroRequestDraftResponse> => {
  const response = await api.post(
    `/api/v1/jobs/opportunities/${opportunityId}/intro-request/draft`,
    data
  );
  return response.data;
};

// ============ COVER LETTER API ============

// Regenerate (and PERSIST) the opportunity's cover letter, optionally forcing a
// language. Omitted / "auto" re-runs the capability-gated auto choice; a 2-letter
// code forces that language. Overwrites the stored template — no separate save.
export const regenerateCoverLetter = async (
  opportunityId: string,
  language?: string
): Promise<EmailTemplate> => {
  const response = await api.post(
    `/api/v1/jobs/opportunities/${opportunityId}/cover-letter/regenerate`,
    { language: language ?? AUTO_LANGUAGE }
  );
  return response.data;
};

// ============ APPLICATIONS API ============

export const getApplications = async (
  params: ApplicationListParams = {}
): Promise<ApplicationListResponse> => {
  const response = await api.get('/api/v1/jobs/applications', { params });
  return response.data;
};

export const updateApplicationStatus = async (
  applicationId: string,
  data: UpdateApplicationStatusRequest
): Promise<UpdateApplicationStatusResponse> => {
  const response = await api.patch(`/api/v1/jobs/applications/${applicationId}/status`, data);
  return response.data;
};

export const archiveApplication = async (
  applicationId: string,
  data: ArchiveApplicationRequest
): Promise<ArchiveApplicationResponse> => {
  const response = await api.post(`/api/v1/jobs/applications/${applicationId}/archive`, data);
  return response.data;
};

export const unarchiveApplication = async (
  applicationId: string
): Promise<void> => {
  await api.delete(`/api/v1/jobs/applications/${applicationId}/archive`);
};

// ============ FOLLOW-UP / REPLY API ============

export const getFollowUpDraft = async (
  applicationId: string
): Promise<FollowUpDraftResponse> => {
  const response = await api.post(`/api/v1/jobs/applications/${applicationId}/follow-up/draft`);
  return response.data;
};

export const sendFollowUp = async (
  applicationId: string,
  data: FollowUpRequest
): Promise<FollowUpResponse> => {
  const response = await api.post(`/api/v1/jobs/applications/${applicationId}/follow-up`, data);
  return response.data;
};

export const getReplyDraft = async (
  applicationId: string,
  language?: string
): Promise<ReplyDraftResponse> => {
  // Optional `?language=` query param (same value rules as elsewhere). Omit it
  // entirely for auto. Body must be the explicit 2nd arg so axios reads the
  // config object as the 3rd — mirrors loginTargetUser/verifyEmail.
  const response = await api.post(
    `/api/v1/jobs/applications/${applicationId}/reply/draft`,
    null,
    language ? { params: { language } } : undefined
  );
  return response.data;
};

export const sendReply = async (
  applicationId: string,
  data: ReplyRequest
): Promise<ReplyResponse> => {
  const response = await api.post(`/api/v1/jobs/applications/${applicationId}/reply`, data);
  return response.data;
};

// ============ INTERVIEW KIT API ============
// One endpoint, two delivery modes. `email` → 202 JSON ack (kit generated in the
// background and emailed to the user's OWN account email). `download` → 200 with
// the raw PDF bytes (generated synchronously; budget ~5–20s). Every call is a
// fresh, billed AI generation — never auto-trigger, only on explicit user click.

export interface InterviewKitEmailResponse {
  status: string; // always "queued"
  message: string; // ready-to-show sentence
  // createdAt / createdAtTimestamp present on the wire but intentionally ignored
}

export interface InterviewKitDownload {
  blob: Blob; // application/pdf
  filename: string; // from Content-Disposition, with a sensible fallback
}

export const emailInterviewKit = async (
  applicationId: string
): Promise<InterviewKitEmailResponse> => {
  const response = await api.post(
    `/api/v1/jobs/applications/${applicationId}/interview-kit`,
    { delivery: 'email' }
  );
  return response.data;
};

export const downloadInterviewKit = async (
  applicationId: string
): Promise<InterviewKitDownload> => {
  try {
    const response = await api.post(
      `/api/v1/jobs/applications/${applicationId}/interview-kit`,
      { delivery: 'download' },
      { responseType: 'blob' }
    );
    const filename =
      parseContentDispositionFilename(response.headers['content-disposition']) ||
      'interview-kit.pdf';
    return { blob: response.data, filename };
  } catch (error: any) {
    // Blob error body → standard JSON envelope (see normalizeBlobDownloadError).
    throw await normalizeBlobDownloadError(error);
  }
};

// ============ RESUME TAILORING API ============
// Casing contract: wrapper fields are camelCase; `content` / `theme` /
// `fixContext` values are raw snake_case passthrough (see types/index.ts).

export const createResumeVersion = async (data: {
  resumeInternalId: string;
  role: string;
  location: string;
}): Promise<ResumeVersion> => {
  const response = await api.post('/api/v1/resumes/versions', data); // 201
  return response.data;
};

export const getResumeVersion = async (id: string): Promise<ResumeVersion> => {
  const response = await api.get(`/api/v1/resumes/versions/${id}`);
  return response.data;
};

// PATCH — every payload field is optional except `expectedContentRevision`:
// omitted fields stay untouched, but the PATCH is optimistic-concurrency
// controlled, and a caller that omits the revision silently overwrites whatever
// another tab or the verifier wrote in between. `content` MUST echo the
// server-assigned section ids exactly as received (stripping them invalidates
// every entry-targeted proposal server-side).
export const updateResumeVersion = async (
  id: string,
  data: Partial<{
    content: ResumeContent;
    fixDecisions: FixDecision[];
    templateId: ResumeTemplateId;
    theme: ResumeTheme;
  }> & { expectedContentRevision: number }
): Promise<ResumeVersion> => {
  const response = await api.patch(`/api/v1/resumes/versions/${id}`, data);
  return response.data;
};

export const applyStructuredResumeProposal = async (
  versionId: string, proposalId: string, expectedContentRevision: number
): Promise<ResumeVersion> => {
  const response = await api.post(
    `/api/v1/resumes/versions/${versionId}/proposals/${proposalId}/apply`,
    { expectedContentRevision }
  );
  return response.data;
};

export const dismissStructuredResumeProposal = async (
  versionId: string, proposalId: string, expectedContentRevision: number
): Promise<ResumeVersion> => {
  const response = await api.post(
    `/api/v1/resumes/versions/${versionId}/proposals/${proposalId}/dismiss`,
    { expectedContentRevision }
  );
  return response.data;
};

export const getResumePreviewHtml = async (id: string): Promise<string> => {
  try {
    const response = await api.get(`/api/v1/resumes/versions/${id}/preview`, {
      responseType: 'text',
    });
    return response.data;
  } catch (error: any) {
    // With responseType 'text', a non-2xx JSON error body arrives as a raw
    // string, so getApiErrorMessage/getApiErrorCode can't read the envelope.
    // Parse it back; leave genuinely non-JSON bodies (proxy HTML) untouched.
    const data = error?.response?.data;
    if (typeof data === 'string') {
      try {
        error.response.data = JSON.parse(data);
      } catch {
        // not JSON — keep the raw string
      }
    }
    throw error;
  }
};

// POST /comments no longer waits for the LLM: it returns a `processing`
// placeholder card immediately (see ResumeFixCard). The caller shows a spinner
// card and polls GET /versions/{id}, swapping the card to its filled/failed form
// by `id`.
export const addResumeComment = async (
  id: string,
  data: { sectionId: string; note: string }
): Promise<ResumeFixCard> => {
  const response = await api.post(
    `/api/v1/resumes/versions/${id}/comments`,
    data
  );
  return response.data;
};

// POST /remove-section returns a ready `op:"delete"` card synchronously (no
// polling). Accepting it splices the element out of `content` and PATCHes — the
// same accept flow as any card, no dedicated server endpoint.
export const removeResumeSection = async (
  id: string,
  data: { sectionId: string }
): Promise<ResumeFixCard> => {
  const response = await api.post(
    `/api/v1/resumes/versions/${id}/remove-section`,
    data
  );
  return response.data;
};

export const approveResumeVersion = async (
  id: string
): Promise<ResumeVersion> => {
  const response = await api.post(`/api/v1/resumes/versions/${id}/approve`);
  return response.data;
};

// Discard an unapproved draft version (DELETE the resource). The editor forks a
// throwaway draft — only approve binds a version to a search — so backing out
// with edits would strand a tuned copy the user can't get back to. Callers use
// this to clean the orphan up, BEST-EFFORT: a failed or not-yet-deployed
// endpoint must never trap the user in the editor, so the call site swallows.
export const deleteResumeVersion = async (id: string): Promise<void> => {
  await api.delete(`/api/v1/resumes/versions/${id}`);
};

// ============ PER-SEARCH AUTO-TAILORING API ============
// Thin passthroughs by design (no try/catch): callers depend on these rejecting
// on failure (e.g. Task 8's bind-on-approve must catch a failed bind).

// Step a search's resume back one version — to the previous tailored version, or
// to the base upload when reverting the first tailor. Returns the updated Search
// (tailoredVersionId/autoTailorStatus cleared only when it lands on the base).
export const revertSearchResume = async (searchId: string): Promise<Search> => {
  const response = await api.post(`/api/v1/searches/${searchId}/revert-resume`);
  return response.data;
};

// Fork an approved tailored version into a fresh editable draft the user can revise.
export const editFromTailored = async (versionId: string): Promise<ResumeVersion> => {
  const response = await api.post(`/api/v1/resumes/versions/${versionId}/edit-from-tailored`);
  return response.data;
};

// Tailor a search's currently-bound (untailored) resume on demand. Returns a DRAFT
// version to open in the editor (with role suggestion cards); approving it there
// binds it to the search. 400 if the resume is already tailored.
export const tailorSearchResume = async (searchId: string): Promise<ResumeVersion> => {
  const response = await api.post(`/api/v1/searches/${searchId}/tailor-resume`);
  return response.data;
};

// Bind a resume version to a search (camelCase `versionId` per backend BindResumeInDTO).
// Returns the updated Search.
export const bindSearchResume = async (searchId: string, versionId: string): Promise<Search> => {
  const response = await api.post(`/api/v1/searches/${searchId}/bind-resume`, { versionId });
  return response.data;
};

export interface OpportunityResumeDownload {
  blob: Blob; // application/pdf or docx
  filename: string; // from Content-Disposition, with a sensible fallback
}

// Download the exact resume outreach would send for this opportunity, for
// applying independently. Same shape as downloadInterviewKit.
export const downloadOpportunityResume = async (
  opportunityId: string,
  format: 'pdf' | 'docx'
): Promise<OpportunityResumeDownload> => {
  try {
    const response = await api.get(
      `/api/v1/jobs/opportunities/${opportunityId}/resume`,
      {
        params: { format },
        responseType: 'blob',
        // A DOCX 404 is expected — legacy resumes predate DOCX rendering, and the
        // UI falls back to offering the PDF (Task 11 UX). Keep it out of Sentry
        // (see monitoring.ts); the promise still rejects so the fallback runs.
        // A missing PDF, by contrast, is a real error and must report.
        ...(format === 'docx' ? { expectedStatuses: [404] } : {}),
      }
    );
    const filename =
      parseContentDispositionFilename(response.headers['content-disposition']) ||
      `resume.${format}`;
    return { blob: response.data, filename };
  } catch (error: any) {
    // Blob error body → standard JSON envelope (see normalizeBlobDownloadError).
    throw await normalizeBlobDownloadError(error);
  }
};

export interface SearchResumeDownload {
  blob: Blob; // application/pdf or docx — the artifact bound to the search
  filename: string; // from Content-Disposition, with a sensible fallback
}

// Download the resume bound to a search — the exact artifact the /searches card
// preview drawer offers. The backend serves the rendered PDF for a tailored
// resume, or the original upload (a .docx stays a .docx) for a base resume; the
// filename in Content-Disposition tells the truth about the type. Reuses the
// same blob + normalize helpers as downloadOpportunityResume.
export const downloadSearchResume = async (
  searchId: string
): Promise<SearchResumeDownload> => {
  try {
    const response = await api.get(`/api/v1/searches/${searchId}/resume`, {
      responseType: 'blob',
    });
    const filename =
      parseContentDispositionFilename(response.headers['content-disposition']) ||
      'resume.pdf';
    return { blob: response.data, filename };
  } catch (error: any) {
    // Blob error body → standard JSON envelope (see normalizeBlobDownloadError).
    throw await normalizeBlobDownloadError(error);
  }
};

// ============ RETRY CONTACT API ============

export const retryContact = async (
  opportunityId: string
): Promise<RetryContactResponse> => {
  const response = await api.post(`/api/v1/jobs/opportunities/${opportunityId}/retry-contact`);
  return response.data;
};

// ============ CONTACT PROPOSAL API ============

// Recipient verification: the opportunity's contact couldn't be confirmed and
// another one is suggested (contactStatus 'proposal', proposedContact). Accept
// makes the suggestion the contact, writes its letter and answers with the
// updated opportunity (contactStatus 'ok'). Decline leaves it 'unreachable',
// keeping the declined suggestion. Both name the suggestion the user was shown
// (`proposedContactId`) and answer 409 `proposal_changed` when it is no longer
// the one on offer — a stale tab can't accept or decline a contact the user
// never saw — or 409 when the opportunity was claimed meanwhile.
export const acceptContactProposal = async (
  opportunityId: string,
  proposedContactId: string
): Promise<Opportunity> => {
  const response = await api.post(
    `/api/v1/jobs/opportunities/${opportunityId}/contact-proposal/accept`,
    { proposedContactId }
  );
  return response.data;
};

export const declineContactProposal = async (
  opportunityId: string,
  proposedContactId: string
): Promise<Opportunity> => {
  const response = await api.post(
    `/api/v1/jobs/opportunities/${opportunityId}/contact-proposal/decline`,
    { proposedContactId }
  );
  return response.data;
};

// ============ ARCHIVES API ============

export const getArchivedApplications = async (
  params: ArchiveListParams = {}
): Promise<ArchiveListResponse> => {
  const response = await api.get('/api/v1/jobs/archives', { params });
  return response.data;
};

// Unified free-text search across all of the user's opportunities (pending /
// applied / archived / rejected). Read-only GET; results are auto-scoped to the
// logged-in user. The optional AbortSignal lets callers cancel in-flight
// requests as the user keeps typing (see useJobSearch).
export const searchJobs = async (
  params: JobSearchParams,
  signal?: AbortSignal
): Promise<JobSearchResponse> => {
  const response = await api.get('/api/v1/jobs/search', { params, signal });
  return response.data;
};

// ============ DASHBOARD API ============

export const getDashboardStats = async (): Promise<DashboardStats> => {
  const response = await api.get('/api/v1/dashboards/stats');
  return response.data;
};

// ============ USER SETTINGS API ============

export const getUserSettings = async (): Promise<UserSettings> => {
  const response = await api.get('/api/v1/users/me/settings');
  return response.data;
};

export const updateUserSettings = async (
  patch: Partial<UserSettingsPatch>
): Promise<UserSettings> => {
  const response = await api.put('/api/v1/users/me/settings', patch);
  return response.data;
};

// ============ PENDING EMAIL IMPORT (manual-forward attach flow) ============
// These endpoints authenticate via the path token only — the configured
// `api` instance would attach a Bearer header from localStorage and redirect
// to /signin on 401, neither of which is right here.

export const getPendingEmailImport = async (
  token: string
): Promise<PendingEmailImport> => {
  const response = await publicApi.get(
    `/api/v1/forwarding/pending/${encodeURIComponent(token)}`
  );
  return response.data;
};

export const attachPendingEmailImport = async (
  token: string,
  applicationId: string
): Promise<AttachPendingEmailResponse> => {
  const response = await publicApi.post(
    `/api/v1/forwarding/pending/${encodeURIComponent(token)}/attach`,
    { applicationId }
  );
  return response.data;
};

// ============ NOTIFICATIONS ============

export const getNotificationToken = async (): Promise<SSETokenResponse> => {
  const response = await api.post('/api/v1/notifications/token');
  return response.data;
};

/**
 * The configured instance itself, for modules that need to issue a request this
 * file does not wrap.
 *
 * Added for `adminApi.ts`, whose calls must set their own `Authorization`
 * header (see that module) — something the named helpers here deliberately do
 * not offer. Purely additive: every existing named export is unchanged, and
 * nothing here should start reaching for this in preference to a named helper.
 */
export default api;
